'use client';

import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import duration from 'dayjs/plugin/duration';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);

import { PropsWithClasses } from '@lamesarv-sdk/types';

interface ICountDownTimeLeft {
  months: number;
  days: number;
  hours: number;
}

export const useCountdown = (endDateStr: string, timezone: string): ICountDownTimeLeft => {
  const [timeLeft, setTimeLeft] = useState<ICountDownTimeLeft>();

  useEffect(() => {
    const endDate = dayjs.tz(endDateStr, timezone);

    const getTimeLeft = () => {
      const now = dayjs();
      const timeDiffMs = endDate.diff(now);
      const timeDiffObj = dayjs.duration(timeDiffMs);

      const countdown = {
        months: Math.floor(timeDiffObj.asMonths()),
        days: timeDiffObj.days(),
        hours: timeDiffObj.hours(),
      };

      setTimeLeft(countdown);
    };

    getTimeLeft();

    const intervalId = setInterval(() => {
      getTimeLeft();
    }, 60 * 1000);

    return () => clearInterval(intervalId);
  }, [endDateStr, timezone]);

  return timeLeft;
};

interface IPromoCountdownProps extends PropsWithClasses {
  endDate: string;
  timezone?: string;
  onStart?: () => void;
  onEnd?: () => void;
  classItem?: string;
}

export const PromoCountDownComponent = (props: IPromoCountdownProps) => {
  const timeLeft = useCountdown(props.endDate, props.timezone ?? 'America/Phoenix');

  return (
    <div className={twMerge(styles.container, props.className)}>
      <div className={twMerge(styles.containerElement, props.classItem)}>
        <div className={twMerge(styles.elementContent, props.classContent)}>
          {timeLeft?.months?.toString()?.padStart(2, '0') ?? '--'}
        </div>
        <div className={twMerge(styles.elementTitle, props.classTitle)}>Months</div>
      </div>
      <div className={twMerge(styles.elementContent, props.classContent)}>:</div>
      <div className={twMerge(styles.containerElement, props.classItem)}>
        <div className={twMerge(styles.elementContent, props.classContent)}>
          {timeLeft?.days?.toString()?.padStart(2, '0') ?? '--'}
        </div>
        <div className={twMerge(styles.elementTitle, props.classTitle)}>Days</div>
      </div>
      <div className={twMerge(styles.elementContent, props.classContent)}>:</div>
      <div className={twMerge(styles.containerElement, props.classItem)}>
        <div className={twMerge(styles.elementContent, props.classContent)}>
          {timeLeft?.hours?.toString()?.padStart(2, '0') ?? '--'}
        </div>
        <div className={twMerge(styles.elementTitle, props.classTitle)}>Hours</div>
      </div>
    </div>
  );
};

const styles = {
  container: 'flex flex-row gap-2 items-start justify-center text-[16px] md:text-[18px] text-white',
  containerElement: 'flex flex-col gap-1 items-center justify-center',
  elementContent: 'font-bold leading-none',
  elementTitle: 'text-[12px] leading-none',
};
